import React from 'react';
import { Link, useMatchRoute } from '@tanstack/react-router';
import { motion, MotionConfig } from 'framer-motion';
import { cn } from '@/lib/utils';
/**
 * iOS-style segmented control with an animated sliding indicator.
 *
 * Active detection uses TanStack Router's `useMatchRoute` — the documented
 * match-resolver that walks the route tree against the current location.
 * More reliable than comparing pathname strings (which had two separate
 * trailing-slash + stale-state bugs that the previous tab-nav components
 * couldn't shake).
 *
 * The selected segment's filled background is rendered as a separate
 * `motion.div` with a shared `layoutId`. When the active tab changes,
 * framer-motion auto-animates the pill's position + size between segments
 * — same trick Vercel / Linear / Notion use for their nav strips.
 */
export const SegmentedTabs = ({ navItems, className, layoutId = 'segmented-tabs-indicator', }) => {
    const matchRoute = useMatchRoute();
    return (<MotionConfig transition={{ type: 'spring', bounce: 0.18, duration: 0.45 }}>
            <div className={cn('relative inline-flex items-center gap-0.5 rounded-full border bg-muted/60 p-1 text-sm', 'shadow-inner dark:bg-muted/30', className)}>
                {navItems.map(({ label, path, params, search, pathname, disabled, badge, hide, exact }) => {
            if (hide)
                return null;
            const segmentBase = 'relative inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors';
            if (disabled) {
                return (<span key={pathname} className={cn(segmentBase, 'cursor-not-allowed text-muted-foreground/60 opacity-70')} aria-disabled="true" title={badge ? `${label} · ${badge}` : label}>
                                <span className="relative z-10 inline-flex items-center gap-1.5">
                                    {label}
                                    {badge && (<span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                                            {badge}
                                        </span>)}
                                </span>
                            </span>);
            }
            // matchRoute returns truthy when the current location
            // resolves to this `to`. Default behaviour is exact match
            // so leaf tabs don't light up when a sibling is active.
            // Tabs that own sub-routes (e.g. A/B Testing has nested
            // /destinations, /smart-links, ...) opt into prefix
            // matching with `exact: false` so the parent stays
            // highlighted while the user browses within.
            const fuzzy = exact === false;
            const matched = Boolean(matchRoute({ to: path, params: params, fuzzy }));
            return (<Link key={pathname} to={path} params={params} search={search} className={cn(segmentBase, matched ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}>
                            {/* Active-segment pill rendered with shared layoutId
                    so framer-motion auto-animates its position +
                    size between segments via FLIP. Brand-colour
                    fill so it's unmistakable on both light and
                    dark themes — the previous `bg-background`
                    blended into the muted container. */}
                            {matched && (<motion.span layoutId={layoutId} className="absolute inset-0 rounded-full bg-primary shadow-sm" aria-hidden="true"/>)}
                            <span className="relative z-10 inline-flex items-center gap-1.5">{label}</span>
                        </Link>);
        })}
            </div>
        </MotionConfig>);
};
